import { ChevronRight, FileText, FolderOpen, HardDrive, Loader2, Upload, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ReceivedOnField, type ReceivedOnFieldProps } from '@/components/documents/ReceivedOnField'
import {
  StagedTableProcessing,
  type TableUploadProcessing,
} from '@/components/documents/StagedTableProcessing'
import { TableProcessingOptions } from '@/components/documents/TableProcessingOptions'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import type { TabularExtractionMode } from '@/services/api/cases'
import { AUDIO_FILE_SUFFIXES } from '@/utils/audio-playback'
import { isVideoFileType, MAX_VIDEO_UPLOAD_BYTES, VIDEO_FILE_SUFFIXES } from '@/utils/video-file'

export interface UploadedFile {
  file: File
  id: string
  relativePath: string | null
  progress: number
  status: 'pending' | 'uploading' | 'uploaded' | 'error'
  error?: string
  documentId?: string
  tableMode?: TabularExtractionMode
}

interface TreeNode {
  name: string
  path: string
  children: TreeNode[]
  file?: UploadedFile
}

export const SUPPORTED_TYPES = [
  '.pdf',
  '.docx',
  '.doc',
  '.xlsx',
  '.xls',
  '.xlsm',
  '.csv',
  '.tsv',
  '.txt',
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.gpx',
  // Audio formats routed through the audio_transcription_case pipeline.
  ...AUDIO_FILE_SUFFIXES,
]

/** What a case upload takes: the types above, plus video recordings (PRD-0074) while the
 * Video Processing extension is on. */
export function uploadTypes(acceptsVideo: boolean): readonly string[] {
  return acceptsVideo ? [...SUPPORTED_TYPES, ...VIDEO_FILE_SUFFIXES] : SUPPORTED_TYPES
}

export const MAX_FILE_SIZE = 100 * 1024 * 1024 // 100MB

/** The size an upload may have: a video its own limit (8 GB), any other file 100 MB. */
export function maxUploadBytes(name: string): number {
  return isVideoFileType(name.split('.').pop()) ? MAX_VIDEO_UPLOAD_BYTES : MAX_FILE_SIZE
}

/** `8 GB` or `100 MB`, for the message a refused file gets. */
export function formatUploadLimit(bytes: number): string {
  return bytes >= 1024 ** 3
    ? `${Math.round(bytes / 1024 ** 3)} GB`
    : `${Math.round(bytes / 1024 ** 2)} MB`
}

export interface CaseDocumentUploaderViewProps {
  caseName: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Current folder path — shown in the destination breadcrumb. */
  basePath?: string
  /** Files currently staged for upload (owned by the connected wrapper). */
  files: UploadedFile[]
  /** Whether an upload is in flight. */
  uploading: boolean
  /** Validates + appends dropped/picked files to the staged list. */
  onAddFiles: (files: File[]) => void
  /** Removes a single staged file by id. */
  onRemoveFile: (id: string) => void
  /** Clears the entire staged list. */
  onClearFiles: () => void
  /** Starts uploading all staged files. */
  onUploadAll: () => void
  /** When provided, renders the "Import from Data Lake" footer affordance. */
  onSwitchToLake?: () => void
  /** Whether the file picker offers video recordings (the Video Processing extension is on). */
  acceptsVideo?: boolean
  /** The batch's optional received day (ADR-0094) and its change; left out, the dialog asks for none. */
  receivedOn?: Pick<ReceivedOnFieldProps, 'value' | 'onChange'>
  tableProcessing?: TableUploadProcessing
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function buildFileTree(files: UploadedFile[]): TreeNode[] {
  const root: TreeNode[] = []

  for (const fileItem of files) {
    const path = fileItem.relativePath || fileItem.file.name
    const parts = path.split('/')

    let currentLevel = root
    let currentPath = ''

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i]
      const isFile = i === parts.length - 1
      currentPath = currentPath ? `${currentPath}/${part}` : part

      if (isFile) {
        currentLevel.push({ name: part, path: currentPath, children: [], file: fileItem })
      } else {
        const existingFolder = currentLevel.find(node => !node.file && node.name === part)
        if (existingFolder) {
          currentLevel = existingFolder.children
        } else {
          const newFolder: TreeNode = { name: part, path: currentPath, children: [] }
          currentLevel.push(newFolder)
          currentLevel = newFolder.children
        }
      }
    }
  }

  return root
}

/**
 * Pure presentation for the case document uploader dialog. Owns only ephemeral
 * UI state (drag highlighting, expanded folders) plus the local file-tree /
 * drag-entry reading helpers; the staged file list, upload state and the upload
 * action itself are supplied by the connected wrapper in `features/documents`.
 */
export function CaseDocumentUploaderView({
  caseName,
  open,
  onOpenChange,
  basePath,
  files,
  uploading,
  onAddFiles,
  onRemoveFile,
  onClearFiles,
  onUploadAll,
  onSwitchToLake,
  acceptsVideo = false,
  receivedOn,
  tableProcessing,
}: CaseDocumentUploaderViewProps) {
  const [dragActive, setDragActive] = useState(false)
  const dragDepthRef = useRef(0)
  const { t } = useTranslation()
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())

  const fileInputRef = useRef<HTMLInputElement>(null)
  const folderInputRef = useRef<HTMLInputElement>(null)

  const toggleFolder = useCallback((path: string) => {
    setExpandedFolders(prev => {
      const next = new Set(prev)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      return next
    })
  }, [])

  const fileTree = useMemo(() => buildFileTree(files), [files])

  // Recursively walk a FileSystemEntry tree, returning Files tagged with their
  // path relative to the dropped root so folder structure is preserved.
  const readEntries = useCallback(async (entry: FileSystemEntry): Promise<File[]> => {
    if (entry.isFile) {
      const fileEntry = entry as FileSystemFileEntry
      return new Promise<File[]>(resolve => {
        fileEntry.file(file => {
          // Re-tag with webkitRelativePath so onAddFiles preserves folder layout.
          const tagged = Object.defineProperty(file, 'webkitRelativePath', {
            value: entry.fullPath.replace(/^\//, ''),
            configurable: true,
          })
          resolve([tagged])
        })
      })
    }
    if (entry.isDirectory) {
      const dirEntry = entry as FileSystemDirectoryEntry
      const reader = dirEntry.createReader()
      const all: File[] = []
      // readEntries() returns at most 100 entries per call; loop until empty.
      const readBatch = (): Promise<FileSystemEntry[]> =>
        new Promise(resolve => reader.readEntries(resolve))
      while (true) {
        const batch = await readBatch()
        if (batch.length === 0) break
        for (const sub of batch) {
          // eslint-disable-next-line no-await-in-loop
          all.push(...(await readEntries(sub)))
        }
      }
      return all
    }
    return []
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      dragDepthRef.current = 0
      setDragActive(false)

      const items = e.dataTransfer.items
      if (items && items.length > 0 && items[0].webkitGetAsEntry) {
        const entries: FileSystemEntry[] = []
        for (let i = 0; i < items.length; i++) {
          const entry = items[i].webkitGetAsEntry()
          if (entry) entries.push(entry)
        }
        Promise.all(entries.map(readEntries)).then(results => {
          onAddFiles(results.flat())
        })
        return
      }

      onAddFiles(Array.from(e.dataTransfer.files))
    },
    [onAddFiles, readEntries]
  )

  // While the dialog is open, swallow page-level drag events so a stray drop
  // outside the drop zone doesn't make the browser navigate to the file.
  useEffect(() => {
    if (!open) return
    const swallow = (e: DragEvent) => {
      e.preventDefault()
    }
    window.addEventListener('dragover', swallow)
    window.addEventListener('drop', swallow)
    return () => {
      window.removeEventListener('dragover', swallow)
      window.removeEventListener('drop', swallow)
    }
  }, [open])

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) onAddFiles(Array.from(e.target.files))
    },
    [onAddFiles]
  )

  const handleFolderInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) onAddFiles(Array.from(e.target.files))
    },
    [onAddFiles]
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(96vw,1100px)] sm:max-w-[1100px] max-h-[calc(100dvh-2rem)] overflow-hidden md:max-h-[92vh]">
        <DialogHeader className="pb-1">
          <DialogTitle className="text-base">{t('cases.documents.upload.title')}</DialogTitle>
          <div className="text-xs text-muted-foreground truncate">
            <span className="text-muted-foreground/60">
              {t('cases.documents.upload.destination')}
            </span>
            <span className="font-mono">
              {caseName}
              {basePath ? ` / ${basePath}` : ''}
            </span>
          </div>
        </DialogHeader>

        <div className="space-y-5 overflow-y-auto max-h-[calc(100dvh-2rem-230px)] md:max-h-[calc(92vh-180px)]">
          <div
            className={`border-2 border-dashed rounded-xl p-5 text-center transition-colors md:p-12 ${
              dragActive
                ? 'border-primary bg-primary/5'
                : 'border-border bg-muted/30 hover:border-border/80'
            }`}
            onDragEnter={e => {
              e.preventDefault()
              dragDepthRef.current += 1
              setDragActive(true)
            }}
            onDragLeave={e => {
              e.preventDefault()
              dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)
              if (dragDepthRef.current === 0) setDragActive(false)
            }}
            onDragOver={e => {
              e.preventDefault()
              e.dataTransfer.dropEffect = 'copy'
            }}
            onDrop={handleDrop}
          >
            <div className={dragActive ? 'pointer-events-none' : undefined}>
              <Upload className="size-10 mx-auto mb-3 text-muted-foreground/50 md:size-14 md:mb-5" />
              <p className="text-base font-medium text-foreground mb-1 md:text-xl">
                {t('cases.documents.upload.dropzone')}
              </p>
              <p className="text-sm text-muted-foreground mb-3 md:mb-6">
                {t('cases.documents.upload.or')}
              </p>
              <div className="flex gap-2 justify-center max-md:flex-wrap">
                <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                  <FileText className="size-4 mr-2" />
                  {t('cases.documents.upload.browseFiles')}
                </Button>
                <Button variant="outline" onClick={() => folderInputRef.current?.click()}>
                  <FolderOpen className="size-4 mr-2" />
                  {t('cases.documents.upload.browseFolder')}
                </Button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={uploadTypes(acceptsVideo).join(',')}
                onChange={handleFileInput}
                className="hidden"
              />
              <input
                ref={el => {
                  folderInputRef.current = el
                  if (el) {
                    el.setAttribute('webkitdirectory', '')
                    el.setAttribute('directory', '')
                    el.setAttribute('mozdirectory', '')
                  }
                }}
                type="file"
                onChange={handleFolderInput}
                className="hidden"
              />
              <p className="text-xs text-muted-foreground/70 mt-4">
                {t('cases.documents.upload.supported')}
              </p>
            </div>
          </div>

          {receivedOn && (
            <ReceivedOnField
              value={receivedOn.value}
              onChange={receivedOn.onChange}
              disabled={uploading}
            />
          )}

          {tableProcessing && (
            <div className="space-y-2">
              <TableProcessingOptions
                value={tableProcessing.value}
                onChange={tableProcessing.onChange}
                disabled={uploading}
              />
              <p className="text-xs text-muted-foreground">
                {t('structuredIngest.processing.upload.scope')}
              </p>
            </div>
          )}

          {files.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">
                  {t('cases.documents.upload.selectedFiles', { count: files.length })}
                </Label>
                {!uploading && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClearFiles}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    {t('cases.documents.upload.clearAll')}
                  </Button>
                )}
              </div>
              <Card className="rounded-md divide-y divide-border max-h-96 overflow-y-auto p-0">
                {fileTree.map(node => (
                  <TreeNodeView
                    key={node.file?.id ?? node.path}
                    node={node}
                    depth={0}
                    controls={{
                      expandedFolders,
                      onToggleFolder: toggleFolder,
                      onRemoveFile,
                      uploading,
                      tableProcessing,
                    }}
                  />
                ))}
              </Card>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 pt-4 border-t max-md:flex-col-reverse max-md:items-stretch">
          {onSwitchToLake ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                onOpenChange(false)
                onSwitchToLake()
              }}
              disabled={uploading}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              <HardDrive className="size-3.5 mr-1.5" />
              {t('cases.documents.upload.importFromLake')}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2 max-md:[&>*]:flex-1">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>
              {t('cases.documents.upload.cancel')}
            </Button>
            <Button onClick={onUploadAll} disabled={files.length === 0 || uploading}>
              {uploading ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  {t('cases.documents.upload.uploading')}
                </>
              ) : (
                t('cases.documents.upload.uploadAll', { count: files.length })
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface TreeControls {
  expandedFolders: Set<string>
  onToggleFolder: (path: string) => void
  onRemoveFile: (id: string) => void
  uploading: boolean
  tableProcessing?: TableUploadProcessing
}

function TreeNodeView({
  node,
  depth,
  controls,
}: {
  node: TreeNode
  depth: number
  controls: TreeControls
}) {
  if (node.file) return <FileNodeView fileItem={node.file} depth={depth} controls={controls} />
  const { expandedFolders, onToggleFolder } = controls
  const isExpanded = expandedFolders.has(node.path)
  const hasChildren = node.children.length > 0
  return (
    <div>
      <div
        className="flex items-center gap-2 p-2 hover:bg-muted/50 cursor-pointer"
        style={{ paddingLeft: `${depth * 16 + 12}px` }}
        onClick={() => hasChildren && onToggleFolder(node.path)}
      >
        <ChevronRight
          className={`size-4 text-muted-foreground shrink-0 transition-transform ${
            isExpanded ? 'rotate-90' : ''
          }`}
        />
        <FolderOpen className="size-4 text-warning shrink-0" />
        <span className="text-sm font-medium text-foreground">{node.name}</span>
        <span className="text-xs text-muted-foreground ml-1">({node.children.length} items)</span>
      </div>
      {isExpanded && (
        <div>
          {node.children.map(child => (
            <TreeNodeView
              key={child.file?.id ?? child.path}
              node={child}
              depth={depth + 1}
              controls={controls}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function FileNodeView({
  fileItem,
  depth,
  controls,
}: {
  fileItem: UploadedFile
  depth: number
  controls: TreeControls
}) {
  const { onRemoveFile, uploading, tableProcessing } = controls
  return (
    <div className="flex items-center gap-3 p-3 min-w-0">
      <div className="shrink-0" style={{ paddingLeft: `${depth * 16 + 28}px` }}>
        <FileText className="size-5 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0 overflow-hidden">
        <p className="text-sm font-medium text-foreground truncate max-w-full">
          {fileItem.file.name}
        </p>
        <FileUploadStatus file={fileItem} />
        {tableProcessing && (
          <StagedTableProcessing
            file={fileItem}
            processing={tableProcessing}
            disabled={uploading}
          />
        )}
      </div>
      {fileItem.status !== 'uploading' && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onRemoveFile(fileItem.id)}
          disabled={uploading}
          className="shrink-0"
        >
          <X className="size-4" />
        </Button>
      )}
    </div>
  )
}

function FileUploadStatus({ file }: { file: UploadedFile }) {
  return (
    <>
      <p className="text-xs text-muted-foreground">
        {formatFileSize(file.file.size)}
        {file.status === 'uploading' && ' • Uploading...'}
        {file.status === 'uploaded' && ' • Uploaded'}
        {file.status === 'error' && ` • Error: ${file.error}`}
      </p>
      {file.status === 'uploading' && (
        <div className="w-full bg-muted rounded-full h-1.5 mt-1">
          <div
            className="bg-primary h-1.5 rounded-full transition-all animate-pulse"
            style={{ width: '60%' }}
          />
        </div>
      )}
    </>
  )
}
